import * as Sentry from '@sentry/nextjs';
import { getRestaurantIdClient } from '@/utils/getRestaurantId';

const FATAL_ERROR_FLUSH_TIMEOUT_MS = 2_000;

type FatalClientErrorContext = {
  source: 'next-global-error';
  digest?: string;
};

function asError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  const error = new Error('Unknown fatal client error');
  (error as Error & { cause?: unknown }).cause = value;
  return error;
}

function browserState() {
  if (typeof window === 'undefined') {
    return { runtime: 'server' };
  }

  return {
    runtime: 'browser',
    path: window.location.pathname,
    visibility_state:
      typeof document === 'undefined' ? 'unknown' : document.visibilityState,
    online: typeof navigator === 'undefined' ? null : navigator.onLine,
  };
}

/** Capture the exact root error-page transition and give Sentry time to send it. */
export function captureFatalClientError(
  value: unknown,
  context: FatalClientErrorContext,
): string | undefined {
  const error = asError(value);
  const restaurantId = getRestaurantIdClient();
  let eventId: string | undefined;

  try {
    eventId = Sentry.withScope((scope) => {
      scope.setLevel('fatal');
      scope.setTag('fatal_client_error', 'true');
      scope.setTag('fatal_error_source', context.source);
      if (context.digest) {
        scope.setTag('next_error_digest', context.digest);
      }
      if (restaurantId) {
        scope.setTag('restaurant_id', restaurantId);
      }
      scope.setContext('fatal_client', {
        ...browserState(),
        digest: context.digest ?? null,
        restaurant_id: restaurantId ?? null,
      });
      return Sentry.captureException(error);
    });
  } catch {
    // The error page must remain renderable even if telemetry is unavailable.
  }

  try {
    void Sentry.flush(FATAL_ERROR_FLUSH_TIMEOUT_MS).catch(() => false);
  } catch {
    // Some browser shutdown paths can fail before flush returns a promise.
  }

  return eventId;
}
